import React, { useState, useRef } from 'react';
import { Camera, Upload, X, Check } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { mediaUrl } from '../utils/media';

const ProfilePictureUpload = ({ currentPicture, onUploadSuccess }) => {
  const [preview, setPreview] = useState(currentPicture || null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG and PNG files are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (img.width < 400 || img.height < 400) {
        toast.error('Image must be at least 400x400 pixels');
        return;
      }
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
    };
    img.src = URL.createObjectURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const response = await api.post('/profile/picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile picture updated!');
      setSelectedFile(null);
      if (onUploadSuccess) onUploadSuccess(response.data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePreview = () => {
    setSelectedFile(null);
    setPreview(currentPicture || null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const displayUrl = preview?.startsWith('blob:') ? preview : mediaUrl(preview);

  return (
    <div className="flex flex-col items-center gap-4" data-testid="profile-picture-upload">
      {/* Preview Circle */}
      <div className="relative group">
        <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white/20 bg-slate-700 flex items-center justify-center shadow-lg">
          {displayUrl ? (
            <img src={displayUrl} alt="Profile" className="w-full h-full object-cover" data-testid="profile-picture-preview" />
          ) : (
            <Camera className="h-10 w-10 text-slate-400" />
          )}
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="absolute bottom-0 right-0 p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-colors"
          data-testid="profile-picture-select-btn"
        >
          <Camera className="h-4 w-4" />
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="profile-picture-file-input"
      />

      <p className="text-xs text-slate-400 text-center">
        JPG or PNG, min 400x400px, max 5MB
      </p>

      {selectedFile && (
        <div className="flex items-center gap-2">
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg font-medium transition-colors disabled:opacity-50"
            data-testid="profile-picture-save-btn"
          >
            {uploading ? (
              <>Uploading...</>
            ) : (
              <><Upload className="h-3.5 w-3.5" />Save Photo</>
            )}
          </button>
          <button
            onClick={handleRemovePreview}
            className="p-2 text-slate-400 hover:text-red-400 transition-colors"
            data-testid="profile-picture-cancel-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfilePictureUpload;
